import { FC, Fragment, useMemo, useState } from "react";

import { Box, Link, Stack } from "@mui/material";
import {
  buildSweepstakesRules,
  SweepstakesRuleParagraph,
  SweepstakesRulesInput,
  SweepstakesRulesModel,
} from "@simplyk/common";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { SWEEPSTAKES_OFFICIAL_RULES_URL } from "@/features/LiveFormEditor/common/sweepstakes";
import { Cross, ExternalLink } from "@/icons/outlined";

const RulesParagraph: FC<{ paragraph: SweepstakesRuleParagraph }> = ({ paragraph }) => (
  <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ whiteSpace: "pre-line" }}>
    {paragraph.segments.map((segment, index) =>
      segment.type === "externalLink" ? (
        <Link key={index} href={segment.href} target="_blank" rel="noopener noreferrer">
          {segment.text}
        </Link>
      ) : segment.type === "strong" ? (
        <Box component="strong" key={index} sx={{ fontWeight: 700 }}>
          {segment.text}
        </Box>
      ) : (
        <Fragment key={index}>{segment.text}</Fragment>
      )
    )}
  </DashboardTypography>
);

export const SweepstakesRulesPreviewContent: FC<{ model: SweepstakesRulesModel }> = ({ model }) => (
  <Stack gap={2.5}>
    <DashboardTypography variant="subtitle1" vibe="text-neutral-intense">
      {model.title}
    </DashboardTypography>
    <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ fontWeight: 700 }}>
      {model.disclosure}
    </DashboardTypography>
    {model.introduction.map((paragraph, index) => (
      <RulesParagraph key={index} paragraph={paragraph} />
    ))}
    {model.sections.map((section) => (
      <Stack key={section.id}>
        <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ fontWeight: 700 }}>
          {section.title}
        </DashboardTypography>
        {section.paragraphs.map((paragraph, index) => (
          <RulesParagraph key={index} paragraph={paragraph} />
        ))}
      </Stack>
    ))}
  </Stack>
);

export const SweepstakesRulesPreview: FC<{ input: SweepstakesRulesInput }> = ({ input }) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);
  const model = useMemo(() => buildSweepstakesRules(input), [input]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={(theme) => ({
          backgroundColor: theme.palette.surface.neutral.supershy,
          borderRadius: 1,
          padding: 3,
        })}
      >
        <Stack flex={1} minWidth={0}>
          <DashboardTypography variant="subtitle1" vibe="text-neutral-intense">
            {t("dashboard", "sweepstakes.rules.title")}
          </DashboardTypography>
          <DashboardTypography variant="body2" vibe="text-neutral-quiet">
            {t("dashboard", "sweepstakes.rules.description")}
          </DashboardTypography>
        </Stack>
        <DashboardButton
          variant="tonal"
          vibe="neutral"
          size="small"
          onClick={() => setOpen(true)}
          data-test="sweepstakes-rules-preview-button"
          sx={{ flexShrink: 0 }}
        >
          {t("dashboard", "sweepstakes.rules.preview")}
        </DashboardButton>
      </Stack>

      <DashboardDialog open={open} onClose={() => setOpen(false)} maxWidth="md" mobileBehaviour="drawer">
        <DashboardDialogBody
          slots={{
            header: (
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={2}
                sx={{ padding: 3, paddingRight: 2, paddingBottom: 1.5 }}
              >
                <DashboardTypography variant="h6" vibe="text-neutral-intense">
                  {t("dashboard", "sweepstakes.rules.modalTitle")}
                </DashboardTypography>
                <DashboardIconButton vibe="neutral" onClick={() => setOpen(false)} aria-label={t("common", "close")}>
                  <Cross />
                </DashboardIconButton>
              </Stack>
            ),
            content: (
              <DashboardDialogContent>
                <Box
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.border.neutral.quiet}`,
                    borderRadius: 1,
                    overflow: "hidden",
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      maxHeight: 560,
                      overflowY: "auto",
                      padding: 2,
                      backgroundColor: theme.palette.surface.neutral.supershy,
                    })}
                  >
                    <SweepstakesRulesPreviewContent model={model} />
                  </Box>
                </Box>
              </DashboardDialogContent>
            ),
            footer: (
              <DashboardDialogFooter sx={{ justifyContent: { md: "space-between" } }}>
                <DashboardButton variant="filled" type="button" onClick={() => setOpen(false)}>
                  {t("dashboard", "sweepstakes.rules.closePreview")}
                </DashboardButton>
                <DashboardButton
                  variant="text"
                  vibe="brand"
                  href={SWEEPSTAKES_OFFICIAL_RULES_URL}
                  target="_blank"
                  endIcon={<ExternalLink />}
                >
                  {t("dashboard", "sweepstakes.rules.learnRegulations")}
                </DashboardButton>
              </DashboardDialogFooter>
            ),
          }}
        />
      </DashboardDialog>
    </>
  );
};
